import { useMemo, useCallback, useState } from "react";
import { Checkbox, Typography, Box } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";

import ToolkitTable from "components/Cards/Tables/ToolkitTable";
import ImagePlaceholder from "components/Image/ImagePlaceholder";
import BulkSelectionModal from "../../../shared/components/bulk-selection-modal";

import { buildShowcaseProductGradeColumns } from "../../../shared/utils/product-grade-fields";

export default function SelectProductsTable({
  data,
  loading,
  totalSize,
  currentPage,
  currentSizePerPage,
  selectedProducts,
  setSelectedProducts,
  handlePaginationChange,
  onSelectAllResults,
}) {
  const [openSelectAllDialog, setOpenSelectAllDialog] = useState(false);
  const [selectAllLoading, setSelectAllLoading] = useState(false);

  const handleSelectProduct = useCallback(
    (product) => {
      setSelectedProducts((prev) => {
        const exists = prev.some((p) => p.Codigo === product.Codigo);
        return exists
          ? prev.filter((p) => p.Codigo !== product.Codigo)
          : [...prev, product];
      });
    },
    [setSelectedProducts],
  );

  const tableData = useMemo(() => {
    return data.map((item) => ({
      ...item,
      selected: selectedProducts.some((p) => p.Codigo === item.Codigo),
    }));
  }, [data, selectedProducts]);

  const pageSelectedCount = tableData.filter((item) => item.selected).length;

  const selectPage = useCallback(() => {
    setSelectedProducts((prev) => {
      const newOnes = data.filter(
        (item) => !prev.some((p) => p.Codigo === item.Codigo),
      );
      return [...prev, ...newOnes];
    });
  }, [data, setSelectedProducts]);

  const deselectPage = useCallback(() => {
    setSelectedProducts((prev) =>
      prev.filter((p) => !data.some((item) => item.Codigo === p.Codigo)),
    );
  }, [data, setSelectedProducts]);

  const deselectAll = useCallback(() => {
    setSelectedProducts([]);
  }, [setSelectedProducts]);

  const selectAll = useCallback(async () => {
    if (!onSelectAllResults) {
      selectPage();
      return;
    }

    setSelectAllLoading(true);
    try {
      const all = (await onSelectAllResults()) ?? [];
      setSelectedProducts((prev) => {
        const newOnes = all.filter(
          (item) => !prev.some((p) => p.Codigo === item.Codigo),
        );
        return [...prev, ...newOnes];
      });
    } finally {
      setSelectAllLoading(false);
    }
  }, [onSelectAllResults, selectPage, setSelectedProducts]);

  const columnModel = useMemo(
    () => [
      ...buildShowcaseProductGradeColumns({
        includeImage: true,
        includeDescricao: true,
      }).map((col) =>
        col.dataField === "FotoPrincipal"
          ? {
              ...col,
              formatter: (_, row) => (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <ImagePlaceholder
                    src={row?.FotoPrincipal ?? row?.foto}
                    alt={`Imagem do produto ${row?.Codigo}`}
                    width="40px"
                    height="40px"
                    widthOnError="40px"
                    heightOnError="40px"
                  />
                </div>
              ),
            }
          : col,
      ),
      {
        dataField: "selected",
        text: "",
        align: "center",
        headerAlign: "center",
        formatter: (_, row) => (
          <Checkbox
            checked={row.selected}
            onChange={() => handleSelectProduct(row)}
          />
        ),
        headerFormatter: () => (
          <Checkbox
            checked={
              tableData.length > 0 && tableData.every((item) => item.selected)
            }
            indeterminate={
              tableData.some((item) => item.selected) &&
              !tableData.every((item) => item.selected)
            }
            onChange={() => setOpenSelectAllDialog(true)}
          />
        ),
      },
    ],
    [handleSelectProduct, tableData],
  );

  return (
    <div>
      {loading ? (
        <Box p={2}>
          <Skeleton
            variant="rect"
            width="100%"
            height={40}
            style={{ marginBottom: 10 }}
          />
          <Skeleton
            variant="rect"
            width="100%"
            height={40}
            style={{ marginBottom: 10 }}
          />
          <Skeleton
            variant="rect"
            width="100%"
            height={40}
            style={{ marginBottom: 10 }}
          />
          <Skeleton
            variant="rect"
            width="100%"
            height={40}
            style={{ marginBottom: 10 }}
          />
          <Skeleton
            variant="rect"
            width="100%"
            height={40}
            style={{ marginBottom: 10 }}
          />
        </Box>
      ) : (
        <>
          <Box mb={2}>
            <Typography variant="body2" color="textSecondary">
              {selectedProducts.length > 0
                ? `${selectedProducts.length} produto(s) selecionado(s)`
                : "Nenhum produto selecionado"}
            </Typography>
          </Box>
          <Box
            sx={{
              minHeight: 360,
              "& .table-responsive": {
                overflowX: "auto",
              },
              "& table": {
                minWidth: 960,
              },
              "& th": {
                whiteSpace: "nowrap",
                fontSize: "0.8rem",
              },
              "& td": {
                fontSize: "0.85rem",
                verticalAlign: "middle",
              },
            }}
          >
            <ToolkitTable
              keyField="Codigo"
              dataTable={tableData}
              columnModel={columnModel}
              hideEmptyMessages
              bootstrapTableProps={{
                remote: true,
                onTableChange: (type, { page, sizePerPage }) => {
                  if (type === "pagination") {
                    handlePaginationChange(page, sizePerPage);
                  }
                },
              }}
              pagination={{
                options: [5, 10, 25, 50, 100],
                totalSize,
                page: currentPage,
                sizePerPage: currentSizePerPage,
                defaultOption: currentSizePerPage,
                showTotal: true,
              }}
            />
          </Box>
          <BulkSelectionModal
            open={openSelectAllDialog}
            onClose={() => (selectAllLoading ? null : setOpenSelectAllDialog(false))}
            title="Selecionar produtos"
            entityLabel="produtos"
            pageCount={data.length}
            pageSelectedCount={pageSelectedCount}
            totalCount={totalSize}
            totalSelectedCount={selectedProducts.length}
            loading={selectAllLoading}
            onSelectPage={selectPage}
            onSelectAll={selectAll}
            onDeselectPage={deselectPage}
            onDeselectAll={deselectAll}
          />
        </>
      )}
    </div>
  );
}
